import type { ColDef, ColGroupDef, ICellRendererParams } from "ag-grid-community";
import type { MouseEventHandler } from "react";
import { SubdirectoryArrowRightIcon } from "@/components/Common/Icons/IconFactory";
import { TEXT_FILTER_OPTIONS } from "@/constants/grid/agGrid";
import type SearchGridSchema from "@/interfaces/SearchGridSchema";
import { AppSettingsService } from "@/service/AppSettingsService";
// import { fiscalYearComparator } from "@/utils/Comparators"; // unused - fiscal year sort disabled
import { buildNumberFilterParams } from "@/utils/Validation";
import {
	createRiskColumnDefs,
	createScorePillParamsFromStatus,
	formatPrivateLabel,
	formatScoreValue,
	getPrivateLabelFilterValue,
	safeFormatScoreDate,
} from "./Helpers";
import PillCellRenderer from "./Pill";

// import YearFilter from "./YearFilter"; // unused - score date filter disabled

const SCM_MAP_PATH_TEMPLATE = "/map/{id}";
const buildNumericFilter = (field: string) => buildNumberFilterParams(field);

const buildScmMapUrl = (scmIdText: string): string | null => {
	const encodedId = encodeURIComponent(scmIdText);
	const mapPath = SCM_MAP_PATH_TEMPLATE.replace("{id}", encodedId);

	try {
		const scmPortalBaseUrl = AppSettingsService.getScmPortalBaseUrl().trim();
		if (!scmPortalBaseUrl) {
			return null;
		}

		const base = new URL(scmPortalBaseUrl);
		return new URL(mapPath, base).toString();
	} catch {
		return null;
	}
};

const renderScmIdLink = (params: ICellRendererParams<SearchGridSchema>): JSX.Element | string => {
	if (params.data?.rowType === "history") {
		return "";
	}
	const scmId = params.value ?? params.data?.scmIDNum;
	if (scmId === null || scmId === undefined || scmId === "") {
		return "";
	}

	const scmIdText = String(scmId);
	const mapUrl = buildScmMapUrl(scmIdText);

	if (!mapUrl) {
		return scmIdText;
	}

	return (
		<a
			href={mapUrl}
			target="_blank"
			rel="noopener noreferrer"
			title="View map (opens in new tab)"
			className="text-blue-600 underline hover:text-blue-700"
		>
			{scmIdText}
		</a>
	);
};

const hasHistoricalVersions = (row?: SearchGridSchema): boolean => {
	return Boolean(row?.hasHistory);
};

export const getMapKey = (row?: Partial<SearchGridSchema>): string => {
	return `${row?.scmIDNum ?? ""}::${row?.itemNumber ?? ""}`;
};

const getHistoryRowKey = (row?: SearchGridSchema): string => {
	return `${row?.scmIDNum ?? ""}:${row?.itemNumber ?? ""}`;
};

const HistoryIndicatorCellRenderer = (params: ICellRendererParams<SearchGridSchema>) => {
	// If this row is an in-line historical child row, render indent icon
	if (params.data?.rowType === "history") {
		return (
			<span className="inline-flex h-full w-full items-center justify-center pt-1 text-gray-400">
				<SubdirectoryArrowRightIcon className="text-2xl" aria-label="Historical version" />
			</span>
		);
	}

	if (!hasHistoricalVersions(params.data)) {
		return null;
	}

	const context = params.context as
		| {
				expandedKeys?: Set<string>;
				loadingKeys?: Set<string>;
				onToggleHistory?: (row: SearchGridSchema) => void;
				expandedHistories?: Record<
					string,
					{ rows: SearchGridSchema[]; loading: boolean; error: string | null }
				>;
				expandedHistoryKey?: string | null;
				historyLoadingKey?: string | null;
				onCloseHistory?: (row?: SearchGridSchema) => void;
		  }
		| undefined;

	const key = getMapKey(params.data);
	const legacyKey = getHistoryRowKey(params.data);

	const isExpanded =
		context?.expandedKeys?.has(key) ??
		context?.expandedKeys?.has(legacyKey) ??
		Boolean(context?.expandedHistories?.[legacyKey]) ??
		context?.expandedHistoryKey === legacyKey;

	const isLoading =
		context?.loadingKeys?.has(key) ??
		context?.loadingKeys?.has(legacyKey) ??
		Boolean(context?.expandedHistories?.[legacyKey]?.loading) ??
		context?.historyLoadingKey === legacyKey;

	const handleCaretClick: MouseEventHandler<HTMLButtonElement> = (event) => {
		event.preventDefault();
		event.stopPropagation();
		if (params.data) {
			context?.onToggleHistory?.(params.data);
		}
	};

	return (
		<span className="inline-flex h-full w-full items-center justify-center pt-1">
			<button
				type="button"
				onClick={handleCaretClick}
				className="inline-flex h-6 w-6 items-center justify-center text-blue-600 hover:text-blue-800"
				aria-label={
					isLoading ? "Loading history" : isExpanded ? "Collapse history" : "Expand history"
				}
				title={isLoading ? "Loading history" : isExpanded ? "Collapse history" : "Expand history"}
				disabled={isLoading}
			>
				<i
					className={`${isLoading ? "icon-sync animate-spin" : isExpanded ? "icon-expand_less" : "icon-expand_more"} text-2xl leading-none`}
					aria-hidden="true"
				/>
			</button>
		</span>
	);
};

export const SupplyChainColumnDefinitions: (
	| ColDef<SearchGridSchema>
	| ColGroupDef<SearchGridSchema>
)[] = [
	{
		field: "hasHistory",
		colId: "historyIndicator",
		headerName: "",
		flex: 0,
		width: 53,
		minWidth: 53,
		maxWidth: 53,
		resizable: false,
		suppressSizeToFit: true,
		suppressMovable: true,
		lockPosition: true,
		autoHeight: false,
		sortable: false,
		filter: false,
		suppressHeaderMenuButton: true,
		headerClass: "border-r border-gray-300",
		cellClass: "flex items-center justify-center",
		cellRenderer: HistoryIndicatorCellRenderer,
	},
	{
		field: "countryRegion",
		headerName: "Selling\nCountry/\nRegion",
		minWidth: 150,
	},
	{
		field: "deptNumber",
		headerName: "Dept",
		minWidth: 100,
		filter: "agNumberColumnFilter",
		filterParams: buildNumericFilter("deptNumber"),
	},
	{
		field: "deptName",
		headerName: "Department Name",
		minWidth: 140,
		initialHide: true,
		filter: true,
		filterParams: {
			defaultOption: "contains",
			filterOptions: TEXT_FILTER_OPTIONS,
		},
	},
	{
		field: "vendorNumber",
		headerName: "Vendor #",
		filter: "agNumberColumnFilter",
		filterParams: buildNumericFilter("vendorNumber"),
		minWidth: 120,
	},
	{
		field: "vendorNameEnglish",
		headerName: "Vendor Name\n(English)",
		minWidth: 150,
		filter: true,
		filterParams: {
			defaultOption: "contains",
			filterOptions: TEXT_FILTER_OPTIONS,
		},
	},
	{
		field: "vendorNameNative",
		headerName: "Vendor Name\n(Native)",
		initialHide: true,
		minWidth: 150,
		filter: true,
		filterParams: {
			defaultOption: "contains",
			filterOptions: TEXT_FILTER_OPTIONS,
		},
	},
	{
		field: "privateLabel",
		headerName: "Private\nLabel",
		minWidth: 150,
		filter: true,
		filterParams: {
			defaultOption: "contains",
			filterOptions: TEXT_FILTER_OPTIONS,
		},
		valueFormatter: formatPrivateLabel,
		// Filter UI values ("N"/"Y") instead of raw DB codes ("NK"/"KS").
		filterValueGetter: getPrivateLabelFilterValue,
	},
	{
		field: "category1Description",
		headerName: "Category\nDescription",
		initialHide: true,
		filter: true,
		filterParams: {
			defaultOption: "contains",
			filterOptions: TEXT_FILTER_OPTIONS,
		},
		minWidth: 200,
	},
	{
		field: "category12Description",
		headerName: "Category12\nDescription",
		initialHide: true,
		filter: true,
		filterParams: {
			defaultOption: "contains",
			filterOptions: TEXT_FILTER_OPTIONS,
		},
		minWidth: 200,
	},
	{
		field: "category123Description",
		headerName: "Category123\nDescription",
		initialHide: true,
		filter: true,
		filterParams: {
			defaultOption: "contains",
			filterOptions: TEXT_FILTER_OPTIONS,
		},
		minWidth: 200,
	},
	{
		field: "itemNumber",
		headerName: "Item #",
		filter: "agNumberColumnFilter",
		filterParams: buildNumericFilter("itemNumber"),
		minWidth: 120,
	},
	{
		field: "itemDescription",
		headerName: "Item\nDescription",
		filter: true,
		filterParams: {
			defaultOption: "contains",
			filterOptions: TEXT_FILTER_OPTIONS,
		},
		minWidth: 150,
	},
	{
		field: "itemDescription2",
		headerName: "Item\nDescription2",
		initialHide: true,
		filter: true,
		filterParams: {
			defaultOption: "contains",
			filterOptions: TEXT_FILTER_OPTIONS,
		},
		minWidth: 150,
	},
	{
		field: "commodity",
		headerName: "Commodity",
		minWidth: 150,
		filter: false,
	},
	// Inject risk-related columns from helper factory
	...createRiskColumnDefs(),
	{
		field: "transparencyRiskScore",
		headerName: "Transparency\nScore",
		headerTooltip: "Sort/Filter: Applies to latest map only",
		minWidth: 180,
		filter: "agNumberColumnFilter",
		filterParams: buildNumericFilter("transparencyRiskScore"),
		cellRenderer: PillCellRenderer,
		cellRendererParams: createScorePillParamsFromStatus("transparencyRiskStatus"),
		valueFormatter: formatScoreValue,
	},
	{
		field: "environmentalRiskScore",
		headerName: "Environmental\nScore",
		headerTooltip: "Sort/Filter: Applies to latest map only",
		minWidth: 160,
		filter: "agNumberColumnFilter",
		filterParams: buildNumericFilter("environmentalRiskScore"),
		cellRenderer: PillCellRenderer,
		cellRendererParams: createScorePillParamsFromStatus("environmentalRiskStatus"),
		valueFormatter: formatScoreValue,
	},
	{
		field: "humanRightsRiskScore",
		headerName: "Human Rights\nScore",
		headerTooltip: "Sort/Filter: Applies to latest map only",
		minWidth: 150,
		filter: "agNumberColumnFilter",
		filterParams: buildNumericFilter("humanRightsRiskScore"),
		cellRenderer: PillCellRenderer,
		cellRendererParams: createScorePillParamsFromStatus("humanRightsRiskStatus"),
		valueFormatter: formatScoreValue,
	},
	{
		field: "scmIDNum",
		headerName: "SCM ID",
		minWidth: 120,
		filter: "agNumberColumnFilter",
		cellRenderer: renderScmIdLink,
		filterParams: buildNumericFilter("scmIDNum"),
	},
	{
		field: "mapVersion",
		headerName: "Map Version #",
		headerTooltip: "SCM Map Version",
		minWidth: 160,
		// Sort and filter disabled - not needed for this column
		sortable: false,
		filter: false,
		// filter: "agNumberColumnFilter",
		// filterParams: buildNumericFilter("mapVersion"),
	},
	{
		field: "fiscalYear",
		headerName: "Fiscal Year",
		minWidth: 130,
		// Sort and filter disabled - not needed for this column
		sortable: false,
		// comparator: fiscalYearComparator,
		filter: false,
		// filter: "agNumberColumnFilter",
		// filterParams: buildNumericFilter("fiscalYear"),
	},
	{
		field: "scoreDate",
		headerName: "Score Date",
		headerTooltip: "Date map was scored",
		minWidth: 150,
		// Sort and filter disabled - not needed for this column
		sortable: false,
		// Display full date to users formatted as MM/DD/YYYY
		valueFormatter: safeFormatScoreDate,

		// Use custom YearFilter (registered in DefaultGrid.frameworkComponents)
		// for strict 4-digit year input and custom UI behavior
		// filter: YearFilter,
		filter: false,

		// Expose a numeric year so the Community custom filter can compare client-side rows.
		// filterValueGetter: (params) => {
		// 	// If data or value missing, return null
		// 	if (!params.data?.scoreDate) return null;
		// 	const d = new Date(params.data.scoreDate);
		// 	// Return numeric year, e.g. 2026
		// 	return Number.isNaN(d.getTime()) ? null : d.getFullYear();
		// },

		// Params for YearFilter component. applyButton false enables auto-apply behavior
		// filterParams: {
		// 	defaultOption: "equals",
		// 	applyButton: false,
		// 	debounceMs: 300,
		// 	filterOptions: ["equals", "notEqual", "lessThan", "greaterThan", "inRange"],
		// },
	},
];

export const createHistoryColumnDefinitions = (): ColDef<SearchGridSchema>[] => [
	{
		field: "overallRiskScore",
		headerName: "Overall Score",
		minWidth: 140,
		valueFormatter: formatScoreValue,
	},
	{
		field: "transparencyRiskScore",
		headerName: "Transparency Score",
		minWidth: 160,
		valueFormatter: formatScoreValue,
	},
	{
		field: "environmentalRiskScore",
		headerName: "Environmental Score",
		minWidth: 160,
		valueFormatter: formatScoreValue,
	},
	{
		field: "humanRightsRiskScore",
		headerName: "Human Rights Score",
		minWidth: 160,
		valueFormatter: formatScoreValue,
	},
	{
		field: "scoreDate",
		headerName: "Map Score Date",
		minWidth: 150,
		valueFormatter: safeFormatScoreDate,
	},
	{
		field: "mapVersion",
		headerName: "Map Version #",
		minWidth: 140,
	},
	{
		field: "fiscalYear",
		headerName: "Fiscal Year",
		minWidth: 130,
	},
];
